import React, { useState } from 'react';
import axios from 'axios';
import KakaoAdBanner from './KakaoAdBanner';

function ReverseCalculator() {

  const [formData, setFormData] = useState({
    current_age: 30,
    retirement_age: 60,
    desired_monthly_income: 3000000,
    annual_return_rate: 7.0,
    pension_yield_rate: 4.0
  });
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // API URL 직접 지정 (프로덕션 환경)
    const apiUrl = 'https://pension-backend-100694510241.asia-northeast3.run.app/api/calculate-reverse-pension';
    
    try {
      const response = await axios.post(apiUrl, formData);
      
      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.message || '계산 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error('API 호출 실패:', err);
      console.error('요청 URL:', apiUrl);
      console.error('응답 상태:', err.response?.status);
      console.error('응답 데이터:', err.response?.data);
      
      if (err.response?.status === 404) {
        setError('API 엔드포인트를 찾을 수 없습니다. Firebase 설정을 확인해주세요.');
      } else if (err.response?.status >= 500) {
        setError('서버 내부 오류가 발생했습니다. Cloud Run 서비스 상태를 확인해주세요.');
      } else if (err.code === 'NETWORK_ERROR' || err.message.includes('Network Error')) {
        setError('네트워크 연결 오류: Cloud Run 서비스에 연결할 수 없습니다.');
      } else {
        setError(`연결 실패: ${err.message}. 개발자 도구 콘솔을 확인해주세요.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* 헤더 */}
      <header className="header">
        <div className="container">
          <h1>라떼비버 노후연금 계산기 - 역산 계산</h1>
          <p>원하는 노후 생활비에서 필요한 월 저축액을 계산합니다</p>
        </div>
      </header>

      {/* 카카오 애드핏 배너 */}
      <div className="container">
        <KakaoAdBanner className="my-4" />
      </div>

      <main className="main-content">
        <div className="container">
          <div className="grid">
            {/* 입력 폼 */}
            <div className="card">
              <h2 className="result-title">📊 계산 조건 입력</h2>
              
              <form onSubmit={handleCalculate}>
                <div className="form-group">
                  <div className="form-row">
                    <div>
                      <label className="form-label">현재 나이</label>
                      <input
                        type="number"
                        name="current_age"
                        value={formData.current_age}
                        onChange={handleInputChange}
                        className="form-input"
                        min="20"
                        max="65"
                        required
                      />
                      <p className="form-help">만 나이</p>
                    </div>

                    <div>
                      <label className="form-label">은퇴 나이</label>
                      <input
                        type="number"
                        name="retirement_age"
                        value={formData.retirement_age}
                        onChange={handleInputChange}
                        className="form-input"
                        min="50"
                        max="80"
                        required
                      />
                      <p className="form-help">은퇴 예정 나이</p>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">원하는 노후 월 생활비 (원)</label>
                  <input
                    type="number"
                    name="desired_monthly_income"
                    value={formData.desired_monthly_income}
                    onChange={handleInputChange}
                    className="form-input"
                    min="100000"
                    step="10000"
                    required
                  />
                  <p className="form-help">은퇴 후 매월 사용하고 싶은 금액</p>
                </div>

                <div className="form-group">
                  <label className="form-label">연 복리 수익률 (%)</label>
                  <input
                    type="number"
                    name="annual_return_rate"
                    value={formData.annual_return_rate}
                    onChange={handleInputChange}
                    className="form-input"
                    min="0"
                    max="20"
                    step="0.1"
                    required
                  />
                  <p className="form-help">저축 기간 동안 예상 수익률</p>
                </div>

                <div className="form-group">
                  <label className="form-label">은퇴 후 포트폴리오 연 배당률 (%)</label>
                  <input
                    type="number"
                    name="pension_yield_rate"
                    value={formData.pension_yield_rate}
                    onChange={handleInputChange}
                    className="form-input"
                    min="0"
                    max="10"
                    step="0.1"
                    required
                  />
                  <p className="form-help">배당주, 월세 등 현금흐름 수익률</p>
                </div>

                {error && (
                  <div className="error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn"
                >
                  {loading ? '계산 중...' : '💡 필요 저축액 계산하기'}
                </button>
              </form>
            </div>

            {/* 결과 표시 */}
            <div className="card">
              <h2 className="result-title">📈 계산 결과</h2>
              
              {!result && (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#6b7280' }}>
                  <p>좌측 폼에서 조건을 입력하고<br/>계산 버튼을 눌러보세요!</p>
                </div>
              )}

              {result && (
                <div className="result-section">
                  {/* 목표 정보 */}
                  <div className="result-card basic-info">
                    <h3 className="result-title">목표 설정</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
                      <div>
                        <span style={{ color: '#6b7280' }}>저축 기간:</span>
                        <span style={{ fontWeight: '600', marginLeft: '8px' }}>{result.total_payment_period}년</span>
                      </div>
                      <div>
                        <span style={{ color: '#6b7280' }}>월 생활비:</span>
                        <span style={{ fontWeight: '600', marginLeft: '8px', color: '#0066CC' }}>
                          {formatNumber(formData.desired_monthly_income)}원
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 필요한 최종 금액 */}
                  <div className="result-card final-amount">
                    <h3 className="result-title">💰 필요한 최종 적립액</h3>
                    <div className="result-amount green">
                      {formatNumber(result.required_final_amount)}원
                    </div>
                    <div className="result-detail">
                      이 금액이 있어야 매월 {formatNumber(formData.desired_monthly_income)}원씩 사용 가능
                    </div>
                  </div>

                  {/* 월 저축액 */}
                  <div className="result-card cash-flow">
                    <h3 className="result-title">📅 매월 저축해야 할 금액</h3>
                    <div className="result-amount orange">
                      월 {formatNumber(result.required_monthly_saving)}원
                    </div>
                    <div style={{ fontSize: '1.125rem', color: '#374151', marginBottom: '8px' }}>
                      총 저축액: {formatNumber(result.total_saving_amount)}원
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      예상 수익: +{formatNumber(result.expected_interest_earned)}원
                      ({((result.expected_interest_earned / result.total_saving_amount) * 100).toFixed(1)}%)
                    </div>
                  </div>


                  {/* 저축 과정 상세 */}
                  <div className="result-card details">
                    <h3 className="result-title">📊 저축 과정 상세</h3>
                    <div>
                      {result.savings_details.map((detail, index) => (
                        <div key={index} className="detail-item">
                          <div style={{ fontSize: '14px' }}>
                            <span style={{ fontWeight: '500' }}>{detail.year}년차</span>
                            <span style={{ color: '#6b7280', marginLeft: '8px' }}>({detail.age}세)</span>
                            <span style={{ color: '#059669', marginLeft: '8px', fontSize: '12px' }}>
                              {detail.progress_percentage}%
                            </span>
                          </div>
                          <div style={{ fontSize: '14px', textAlign: 'right' }}>
                            <div style={{ fontWeight: '600', color: '#059669' }}>
                              {formatNumber(detail.accumulated_amount)}원
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>
                              수익 +{formatNumber(detail.interest_earned)}원
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* 설명 섹션 */}
      <section className="info-section">
        <div className="container">
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '32px' }}>
            💡 역산 계산 방식 설명
          </h2>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon blue">
                <span>📊</span>
              </div>
              <h3 className="info-title">목표 설정</h3>
              <p className="info-text">
                원하는 노후 생활비를 먼저 정하고, 이를 위해 필요한 자금을 역산합니다.
              </p>
            </div>
            <div className="info-item">
              <div className="info-icon green">
                <span>💰</span>
              </div>
              <h3 className="info-title">최종 적립액 계산</h3>
              <p className="info-text">
                월 생활비 ÷ 배당률로 필요한 최종 적립액을 계산합니다.
              </p>
            </div>
            <div className="info-item">
              <div className="info-icon orange">
                <span>📈</span>
              </div>
              <h3 className="info-title">월 저축액 도출</h3>
              <p className="info-text">
                복리 수익을 고려하여 매월 저축해야 할 금액을 계산합니다.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ReverseCalculator;